import React, {FocusEvent, ReactNode, useEffect, useRef, useState} from 'react'

import Popover from '../overlays/Popover.js'
import {ListItem} from '../surfaces/index.js'
import {zIndex} from '../styles.js'
import DevBranchBadge from './DevBranchBadge.js'
import SideNavItem, {SideNavContext} from './SideNavItem.js'
import {sideNavColors, sideNavSizes} from './styles.js'
import {NavItem, Navigation} from './types.js'

interface SideNavProps {
    navigation: Navigation
    /** Branding of the reseller: the logo and the colour of the active row, undefined while loading */
    branding?: {color?: string, squareLogo?: string} | null
    history: {push: (path: string) => void}
    /** Rendered above the end items (the branch selector), use useSideNav in it for the state of the rail */
    endSlot?: ReactNode
}

// Only keyboard focus expands the rail: a row clicked with the mouse keeps its focus after the mouse has left
function isFocusVisible(element: EventTarget) {
    try {
        return element instanceof Element && element.matches(':focus-visible')
    } catch {
        // Browsers without :focus-visible
        return true
    }
}

const initials = (name?: string) => (name || '?').split(' ').filter(Boolean).map((part) => part[0]).slice(0, 2).join('').toUpperCase()

/**
 * The rail has no group rows: the items of a group are listed like the other items, in sections split by a line (a
 * group is a section, and so are the plain items between groups)
 */
function sections(items: NavItem[]) {
    const result: NavItem[][] = []
    let previousWasGroup = true

    for (const item of items) {
        if (item.children) {
            result.push(item.children)
        } else if (previousWasGroup) {
            result.push([item])
        } else {
            result[result.length - 1].push(item)
        }

        previousWasGroup = !!item.children
    }

    return result
}

/**
 * The navigation as a dark rail at the left of the screen, rendered from a Navigation (types.ts) like TopNav. The rail
 * shows the icons; while hovered, focused with the keyboard or while a menu opened from it is open, it expands over the
 * page (the page doesn't move) and shows the labels. Groups have no row of their own: their items are listed, with a
 * line between the groups. At the bottom: endSlot, the end items and the account with its menu.
 *
 * The page next to it needs a left padding of sideNavSizes.width.
 */
export default function SideNav({navigation, branding, history, endSlot}: SideNavProps) {
    const {items, endItems, account} = navigation

    const [hover, setHover] = useState(false)
    const [focused, setFocused] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const collapseTimer = useRef<number>()
    const navRef = useRef<HTMLElement>(null)

    const expanded = hover || focused || menuOpen

    useEffect(() => () => window.clearTimeout(collapseTimer.current), [])

    const onMouseEnter = () => {
        window.clearTimeout(collapseTimer.current)
        setHover(true)
    }

    const onMouseLeave = () => {
        collapseTimer.current = window.setTimeout(() => setHover(false), sideNavSizes.collapseDelay)
    }

    const onFocus = (event: FocusEvent) => {
        if (isFocusVisible(event.target)) {
            setFocused(true)
        }
    }

    const onBlur = (event: FocusEvent) => {
        if (!navRef.current?.contains(event.relatedTarget as Node)) {
            setFocused(false)
        }
    }

    const renderItem = (item: NavItem) => (
        <SideNavItem key={item.key} icon={item.icon} title={item.title} target={item.target} onClick={item.onClick} active={item.active} />
    )

    const section = {display: 'flex', flexDirection: 'column', gap: sideNavSizes.gap, padding: `0 ${sideNavSizes.padding}px`} as const

    return (
        <SideNavContext.Provider value={{expanded, branding, history, onMenuToggle: setMenuOpen}}>
            <DevBranchBadge />

            <nav
                ref={navRef}
                aria-label='Hoofdmenu'
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onFocus={onFocus}
                onBlur={onBlur}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    bottom: 0,
                    zIndex: zIndex.nav,
                    display: 'flex',
                    flexDirection: 'column',
                    width: expanded ? sideNavSizes.expandedWidth : sideNavSizes.width,
                    padding: `${sideNavSizes.padding}px 0`,
                    boxSizing: 'border-box',
                    background: sideNavColors.background,
                    boxShadow: expanded ? sideNavColors.shadow : 'none',
                    overflow: 'hidden',
                    transition: `width ${sideNavSizes.transition}, box-shadow ${sideNavSizes.transition}`
                }}
            >
                <div style={{...section, flexShrink: 0, marginBottom: 12}}>
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', width: sideNavSizes.iconColumn, height: sideNavSizes.rowHeight}}>
                        {branding === undefined ?
                            <div style={{width: sideNavSizes.logoSize, height: sideNavSizes.logoSize, borderRadius: 4, background: sideNavColors.avatar}} /> :
                            <img style={{maxWidth: sideNavSizes.logoSize, maxHeight: sideNavSizes.logoSize}} src={branding?.squareLogo || '/images/VELOYDLOGOSMALL.png'} alt='Logo' />
                        }
                    </div>
                </div>

                {/* Scrolls when the items don't fit, only while expanded so the rail shows no scrollbar */}
                <div style={{...section, flex: 1, minHeight: 0, overflowX: 'hidden', overflowY: expanded ? 'auto' : 'hidden'}}>
                    {sections(items).map((section, index) => (
                        <React.Fragment key={section[0].key}>
                            {index > 0 && <div style={{height: 1, flexShrink: 0, background: sideNavColors.divider, margin: '6px 4px'}} />}
                            {section.map(renderItem)}
                        </React.Fragment>
                    ))}
                </div>

                <div style={{...section, flexShrink: 0, paddingTop: sideNavSizes.gap}}>
                    {endSlot}

                    {endItems.map(renderItem)}

                    <div style={{height: 1, background: sideNavColors.divider, margin: '6px 4px'}} />

                    <Popover
                        placement='right'
                        popoverStyle={{marginLeft: sideNavSizes.padding}}
                        onToggle={setMenuOpen}
                        content={
                            <div>
                                {account.links.map((link) => (
                                    <ListItem key={link.key} onClick={link.onClick || (() => history.push(link.target))}>
                                        {link.icon && <i style={{marginRight: 12, fontSize: 20}} className={link.icon} />}
                                        <div>{link.title}</div>
                                    </ListItem>
                                ))}
                            </div>
                        }
                    >
                        <SideNavItem
                            iconNode={
                                <span style={{display: 'flex', alignItems: 'center', justifyContent: 'center', width: sideNavSizes.avatarSize, height: sideNavSizes.avatarSize, borderRadius: '50%', background: sideNavColors.avatar, fontSize: 12, fontWeight: 600, color: sideNavColors.textActive}}>
                                    {initials(account.title)}
                                </span>
                            }
                            title={account.title || 'Account'}
                            subTitle={account.subTitle}
                            trailing={<i className='mdi mdi-chevron-right' />}
                            hasPopup
                        />
                    </Popover>
                </div>
            </nav>
        </SideNavContext.Provider>
    )
}
